// Reusable install affordance. Renders an "Install" button on Android/desktop
// (when a deferred prompt is available), an "Add to Home Screen" hint on iOS,
// and nothing when already installed / not applicable. Self-updates when the
// browser's beforeinstallprompt fires after mount.
import { el, toast } from "../lib/dom";
import { canInstall, promptInstall, onInstallChange } from "../lib/install";
import { isIOS, isStandalone } from "../lib/permissions";

const DISMISS_KEY = "riverghost:installDismissed";

export function installPrompt(opts: { dismissible?: boolean; compact?: boolean } = {}): HTMLElement {
  const holder = el("div");

  function render() {
    if (isStandalone() || (opts.dismissible && sessionStorage.getItem(DISMISS_KEY))) {
      holder.replaceChildren();
      return;
    }
    if (canInstall()) {
      const btn = el("button.btn", {}, "📲 Install the field kit") as HTMLButtonElement;
      btn.addEventListener("click", async () => {
        btn.disabled = true;
        const ok = await promptInstall();
        if (!ok) { btn.disabled = false; toast("Install dismissed. You can do it later from Agent.", "warn"); }
      });
      holder.replaceChildren(wrap(btn));
    } else if (isIOS()) {
      holder.replaceChildren(wrap(
        el("p.tiny.dim", { style: "margin:0" },
          "📲 Install: tap the Share icon → “Add to Home Screen” for the full field kit."),
      ));
    } else {
      holder.replaceChildren();
    }
  }

  function wrap(inner: HTMLElement): HTMLElement {
    if (!opts.dismissible) return opts.compact ? inner : el("div", {}, inner);
    const x = el("button.hud__bell", { style: "min-width:32px;min-height:32px;font-size:14px" }, "✕");
    x.addEventListener("click", () => { sessionStorage.setItem(DISMISS_KEY, "1"); render(); });
    return el("div.card", { style: "display:flex;gap:10px;align-items:center;justify-content:space-between;border-color:var(--amber)" },
      el("div", { style: "flex:1" }, inner), x);
  }

  // Re-render if the deferred prompt arrives after mount. (Screens replace
  // #app wholesale on navigation; a stale listener just no-ops on a detached
  // node, which is harmless for a short-lived party app.)
  onInstallChange(render);
  render();
  return holder;
}
